/* eslint-disable no-restricted-imports */
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Field, Form, Formik } from 'formik';
import { pick } from 'ramda';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { object, string } from 'yup';

import { Checkbox } from 'components/Forms/Inputs';

import { CREDIT_CARD_PAYMENT_METHOD, PAYMENT_METHOD } from 'constants/all';

import {
  setCardDetails,
  setCustomerInfoStep,
  setPointOfContact,
  validateCC,
} from '../../data/actions';
import {
  getCard,
  getConfirmationEmail,
  getPaymentMethod,
  getPointOfContact,
  getWithConfirmation,
} from '../../data/reducer';
import CreditCardSelector from './CreditCardSelector';
import PurchasingDepartmentForm from './PurchasingDepartmentForm';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  .StripeElement {
    background: #efefef;
    border-radius: 2px;
    height: 40px;
    padding-left: 4px;
    padding-top: 12px;
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  .StripeElement {
    background: #efefef;
    border-radius: 2px;
    height: 40px;
    padding-left: 4px;
    padding-top: 12px;
  }
`;

const SendConfirmationWrapper = styled.div`
  border-top: 1px solid #ddd;
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  margin-right: -22px;
  margin-left: -22px;
  padding-left: 22px;
  padding-right: 22px;
  padding-top: 14px;
  .ant-form-item {
    margin-bottom: 0px;
  }
`;

const PaymentDetails = forwardRef<HTMLFormElement>((_, ref) => {
  const dispatch = useDispatch();
  const card = useSelector(getCard);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const paymentMethod = useSelector(getPaymentMethod);
  const pointOfContact = useSelector(getPointOfContact);
  const withConfirmation = useSelector(getWithConfirmation);
  const stripe = useStripe();
  const elements = useElements();
  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(ref, () => ({
    submit: () => {
      formRef.current?.handleSubmit();
    },
  }));

  const handleSubmit = async (values, { setStatus, setSubmitting }) => {
    if (
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
      values.creditCardPaymentMethod === CREDIT_CARD_PAYMENT_METHOD.NEW
    ) {
      try {
        let stripeToken = null;
        let card = null;
        dispatch(validateCC.request());
        const cardElement = elements.getElement(CardElement);
        const { error, token } = await stripe.createToken(cardElement);
        if (error) {
          throw error;
        } else if (token) {
          dispatch(validateCC.success());
          stripeToken = token.id;
          card = pick(['brand', 'last4'], token.card);
          const { pointOfContact, confirmationEmail, withConfirmation } =
            values;
          dispatch(setCardDetails({ stripeToken, card }));
          dispatch(
            setPointOfContact({
              pointOfContact,
              confirmationEmail,
              withConfirmation,
            }),
          );
          dispatch(setCustomerInfoStep({ step: 'final' }));
        }
      } catch (error) {
        dispatch(validateCC.error());
        setStatus(error.message);
      } finally {
        setSubmitting(false);
      }
    } else {
      const { pointOfContact, confirmationEmail, withConfirmation } = values;
      dispatch(
        setPointOfContact({
          pointOfContact,
          confirmationEmail,
          withConfirmation,
        }),
      );
      dispatch(setCustomerInfoStep({ step: 'final' }));
    }
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        pointOfContact,
        confirmationEmail,
        withConfirmation,
        creditCardPaymentMethod: card
          ? CREDIT_CARD_PAYMENT_METHOD.EXISTING
          : CREDIT_CARD_PAYMENT_METHOD.NEW,
      }}
      validationSchema={object().shape({
        confirmationEmail: string().when('pointOfContact', {
          is: (value) => {
            return value !== '' && value !== null && value !== undefined;
          },
          then: string()
            .email('Email is not valid')
            .required('E-mail required'),
          otherwise: string().notRequired(),
        }),
        pointOfContact: string().notRequired(),
      })}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form autoComplete="none">
          <Wrapper>
            {paymentMethod === PAYMENT_METHOD.CREDIT_CARD && card && (
              <CreditCardSelector card={card} />
            )}
            {((paymentMethod === PAYMENT_METHOD.CREDIT_CARD && !card) ||
              (paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
                values.creditCardPaymentMethod ===
                  CREDIT_CARD_PAYMENT_METHOD.NEW)) && <CardElement />}
            <PurchasingDepartmentForm />
            <SendConfirmationWrapper>
              <Field
                component={Checkbox}
                name="withConfirmation"
                label={<span>Send Confirmation to Customer</span>}
              />
            </SendConfirmationWrapper>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
});

PaymentDetails.displayName = 'PaymentDetails';

export default PaymentDetails;
